import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { TIDS } from "@/constants/testIds";
import { CheckCircle2, MapPin, Loader2, ArrowRight, Radio } from "lucide-react";
import LiveMap from "@/components/LiveMap";

export default function BookingConfirmation() {
  const { reference } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const nav = useNavigate();
  const [booking, setBooking] = useState(null);
  const [paying, setPaying] = useState(false);
  const [tracking, setTracking] = useState(null);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const q = `?token=${encodeURIComponent(token)}`;
        const { data } = await api.get(`/bookings/public/${reference}${q}`);
        if (alive) setBooking(data);
        const { data: t } = await api.get(`/bookings/public/${reference}/tracking${q}`);
        if (alive) setTracking(t);
      } catch { toast.error("Booking not found"); }
    };
    load();
    const i = setInterval(load, 8000);
    return () => { alive = false; clearInterval(i); };
  }, [reference, token]);

  const payWithStripe = async () => {
    setPaying(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        booking_id: booking.id, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start payment");
      setPaying(false);
    }
  };

  if (!booking) {
    return <div className="min-h-screen bg-[#090D14] grid place-items-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  }

  return (
    <div className="min-h-screen bg-[#090D14] text-white">
      <Header />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-28 pb-20">
        <div className="glass-card rounded-3xl p-8 sm:p-10 gold-border fade-up">
          <div className="flex items-center gap-3 text-amber-400">
            <CheckCircle2 className="w-6 h-6" />
            <span className="text-caption">Booking Confirmed</span>
          </div>
          <div className="mt-4 flex flex-wrap items-baseline gap-3">
            <h1 className="font-display text-4xl sm:text-5xl font-extrabold">Ref</h1>
            <span data-testid={TIDS.confirmation.reference} className="font-mono text-4xl sm:text-5xl font-extrabold text-amber-400">#{booking.reference}</span>
          </div>
          <p className="mt-4 text-slate-400 max-w-lg">Thanks {booking.rider_name}. Your ride is in the queue — our dispatch team is assigning a driver.</p>

          <div className="mt-8 grid sm:grid-cols-2 gap-6 border-t border-white/5 pt-6">
            <Row label="From" icon={MapPin} value={booking.pickup_location} />
            <Row label="To" icon={MapPin} value={booking.dropoff_location} />
            <Row label="When" value={booking.pickup_time_display} />
            <Row label="Passengers" value={booking.passenger_count} />
            <Row label="Service" value={booking.service_name} />
            <Row label="Estimated fare" value={`NZ$ ${Number(booking.fare_estimate).toFixed(2)}`} highlight />
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            <Button data-testid={TIDS.confirmation.payNow} onClick={payWithStripe} disabled={paying || booking.payment_status === "paid"} className="btn-gold rounded-full h-12 px-6">
              {paying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {booking.payment_status === "paid" ? "Paid" : "Pay now with card"} <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
            <Button data-testid={TIDS.confirmation.back} onClick={() => nav("/")} variant="outline" className="rounded-full h-12 px-6 bg-transparent border-white/15 text-white hover:bg-white/5">Back to home</Button>
          </div>
          <p className="text-xs text-slate-500 mt-6">Prefer cash or EFTPOS? Just tell your driver on pickup.</p>
        </div>

        {(tracking?.driver_name || booking.driver_name) && (
          <div className="mt-6 glass-card rounded-3xl p-6 gold-border">
            <div className="flex items-center gap-3 mb-4">
              <Radio className="w-4 h-4 text-emerald-400 animate-pulse" />
              <span className="text-caption">Live tracking · {tracking?.driver_name || booking.driver_name}</span>
              {tracking?.plate && <span className="ml-auto text-xs font-mono text-slate-400">{tracking.plate}</span>}
            </div>
            <LiveMap lat={tracking?.lat} lng={tracking?.lng} driverName={tracking?.driver_name || booking.driver_name} vehicle={tracking?.vehicle} plate={tracking?.plate} />
            {!tracking?.lat && <p className="text-xs text-slate-500 mt-3 text-center">Waiting for driver to go live…</p>}
          </div>
        )}
      </div>
    </div>
  );
}

const Row = ({ label, value, icon: Icon, highlight }) => (
  <div>
    <div className="text-xs text-slate-500 uppercase tracking-wider flex items-center gap-1.5">{Icon && <Icon className="w-3 h-3" />} {label}</div>
    <div className={`mt-1 font-medium ${highlight ? "text-amber-400 font-display text-xl" : "text-white"}`}>{value}</div>
  </div>
);
